import React, { useEffect, useRef, useState } from 'react';
import { useTradingContext } from '../../context/TradingContext';
import { BarChart2, Maximize2, Settings } from 'lucide-react';
import { getApiBaseUrl } from '../../services/api';
import { createChart, CrosshairMode, CandlestickSeries } from 'lightweight-charts';

export default function TradingChart() {
  const { selectedPair, currentMarketData } = useTradingContext();
  const chartContainerRef = useRef(null);
  const [chartError, setChartError] = useState(false);
  const [timeFrame, setTimeFrame] = useState('15');
  const lastBarRef = useRef(null);
  const chartRef = useRef(null);
  const candleSeriesRef = useRef(null);

  const timeFrames = [
    { label: '1m', value: '1' },
    { label: '5m', value: '5' },
    { label: '15m', value: '15' },
    { label: '1H', value: '60' },
    { label: '4H', value: '240' },
    { label: '1D', value: 'D' },
    { label: '1W', value: 'W' }
  ];

  // Handle Real-time updates
  useEffect(() => {
    if (currentMarketData && candleSeriesRef.current && lastBarRef.current && chartRef.current) {
      const currentPrice = parseFloat(currentMarketData.price);
      
      const bar = lastBarRef.current;
      const now = Math.floor(Date.now() / 1000);
      
      // Determine the interval in seconds
      let intervalSec = 15 * 60;
      if (timeFrame === '1') intervalSec = 60;
      else if (timeFrame === '5') intervalSec = 5 * 60;
      else if (timeFrame === '15') intervalSec = 15 * 60;
      else if (timeFrame === '60') intervalSec = 60 * 60;
      else if (timeFrame === '240') intervalSec = 4 * 60 * 60;
      else if (timeFrame === 'D') intervalSec = 24 * 60 * 60;
      else if (timeFrame === 'W') intervalSec = 7 * 24 * 60 * 60;

      // Current candle time bucket
      const currentBarTime = Math.floor(now / intervalSec) * intervalSec;

      if (currentBarTime > bar.time) {
        // Create a new bar
        const newBar = {
          time: currentBarTime,
          open: bar.close, // open at previous close
          high: currentPrice,
          low: currentPrice,
          close: currentPrice,
        };
        lastBarRef.current = newBar;
        candleSeriesRef.current.update(newBar);
      } else {
        // Update existing bar
        bar.close = currentPrice;
        bar.high = Math.max(bar.high, currentPrice);
        bar.low = Math.min(bar.low, currentPrice);
        
        candleSeriesRef.current.update(bar);
      }
    }
  }, [currentMarketData, timeFrame]);

  useEffect(() => {
    if (!selectedPair || !selectedPair.symbol || !chartContainerRef.current) return;
    
    // Clear container
    chartContainerRef.current.innerHTML = '';
    
    const chart = createChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth,
      height: chartContainerRef.current.clientHeight,
      layout: {
        background: { type: 'solid', color: '#020617' },
        textColor: '#64748b',
      },
      grid: {
        vertLines: { color: 'rgba(255, 255, 255, 0.03)' },
        horzLines: { color: 'rgba(255, 255, 255, 0.03)' },
      },
      crosshair: {
        mode: CrosshairMode.Normal,
      },
      rightPriceScale: {
        borderColor: 'rgba(255, 255, 255, 0.1)',
      },
      timeScale: {
        borderColor: 'rgba(255, 255, 255, 0.1)',
        timeVisible: true,
        secondsVisible: false,
      },
    });
    
    chartRef.current = chart;

    const candleSeries = chart.addSeries(CandlestickSeries, {
      upColor: '#10b981',
      downColor: '#f43f5e',
      borderDownColor: '#f43f5e',
      borderUpColor: '#10b981',
      wickDownColor: '#f43f5e',
      wickUpColor: '#10b981',
    });
    
    candleSeriesRef.current = candleSeries;

    const loadHistory = async () => {
      try {
        setChartError(false);
        const symbol = selectedPair.symbol.includes(':') 
          ? selectedPair.symbol.split(':')[1] 
          : selectedPair.symbol;
          
        const apiBaseUrl = getApiBaseUrl();
        const to = Math.floor(Date.now() / 1000);
        
        let secondsPerBar = 15 * 60;
        if (timeFrame === '1') secondsPerBar = 60;
        else if (timeFrame === '5') secondsPerBar = 5 * 60;
        else if (timeFrame === '15') secondsPerBar = 15 * 60;
        else if (timeFrame === '60') secondsPerBar = 60 * 60;
        else if (timeFrame === '240') secondsPerBar = 4 * 60 * 60;
        else if (timeFrame === 'D') secondsPerBar = 24 * 60 * 60;
        else if (timeFrame === 'W') secondsPerBar = 7 * 24 * 60 * 60;
        
        const from = to - (1000 * secondsPerBar);

        const res = await fetch(`${apiBaseUrl}/udf/history?symbol=${symbol}&resolution=${timeFrame}&from=${from}&to=${to}`);
        const data = await res.json();
        
        if (data.s === 'ok' && data.t && data.t.length > 0) {
          const formattedData = [];
          
          for (let i = 0; i < data.t.length; i++) {
            formattedData.push({
              time: data.t[i],
              open: data.o[i],
              high: data.h[i],
              low: data.l[i],
              close: data.c[i],
            });
          }
          
          candleSeries.setData(formattedData);
          lastBarRef.current = { ...formattedData[formattedData.length - 1] };
        }
      } catch (err) {
        console.error("Erreur chargement history:", err);
        setChartError(true);
      }
    };

    loadHistory();

    const handleResize = () => {
      if (chartContainerRef.current && chartRef.current) {
        try {
          chartRef.current.applyOptions({
            width: chartContainerRef.current.clientWidth,
            height: chartContainerRef.current.clientHeight,
          });
        } catch (e) {}
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (chartRef.current) {
        try {
          chartRef.current.remove();
        } catch (e) {}
        chartRef.current = null;
        candleSeriesRef.current = null;
      }
    };
  }, [selectedPair, timeFrame]);

  if (!selectedPair) {
    return (
      <div className="w-full h-full min-h-[400px] flex items-center justify-center bg-slate-950 rounded-2xl border border-white/5">
        <div className="text-center">
          <BarChart2 className="mx-auto mb-4 text-slate-800 animate-pulse" size={48} />
          <p className="text-slate-600 font-black uppercase tracking-widest text-[10px]">Sélectionnez une paire</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col bg-slate-950 rounded-2xl border border-white/5 overflow-hidden shadow-2xl">
      <div className="flex items-center justify-between px-2 sm:px-4 py-2 border-b border-white/5 bg-slate-900/40 backdrop-blur-md z-20 overflow-x-auto scrollbar-hide">
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          <div className="flex items-center bg-slate-950/50 rounded-xl p-0.5 sm:p-1 border border-white/5 shadow-inner">
            {timeFrames.map((tf) => (
              <button
                key={tf.value}
                onClick={() => setTimeFrame(tf.value)}
                className={`px-2 sm:px-3 py-1 rounded-lg text-[9px] sm:text-[10px] font-black uppercase tracking-tight transition-all duration-200 ${
                  timeFrame === tf.value 
                    ? 'bg-indigo-500 text-white shadow-lg shadow-indigo-500/40 scale-105' 
                    : 'text-slate-500 hover:text-slate-200 hover:bg-white/5'
                }`}
              >
                {tf.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2 sm:gap-4 shrink-0">
            <div className="hidden xs:flex items-center gap-2 px-2 sm:px-3 py-1.5 bg-slate-950/50 rounded-lg border border-white/5 text-[8px] sm:text-[9px] font-black text-slate-500 uppercase tracking-[0.2em]">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)] animate-pulse" />
                Live
            </div>
            <button className="p-1.5 sm:p-2 rounded-xl text-slate-500 hover:text-white hover:bg-white/5 transition-all">
                <Maximize2 size={14} />
            </button>
        </div>
      </div> 

      <div className="flex-1 min-h-[400px] sm:min-h-[500px] lg:min-h-0 relative bg-slate-950">
        {chartError ? (
          <div className="w-full h-full flex flex-col items-center justify-center gap-4">
            <div className="p-5 rounded-full bg-rose-500/10 text-rose-500">
                <Settings size={32} className="animate-spin-slow" />
            </div>
            <p className="text-slate-500 text-[10px] font-black uppercase tracking-widest">Erreur de flux de données</p>
            <button onClick={() => window.location.reload()} className="px-5 py-2.5 bg-indigo-500 text-white rounded-xl text-[10px] font-black uppercase tracking-widest shadow-lg shadow-indigo-500/20 hover:bg-indigo-600 transition-all">Relancer le terminal</button>
          </div>
        ) : (
          <div ref={chartContainerRef} className="absolute inset-0 w-full h-full" />
        )}
      </div>
    </div>
  );
}